import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import googleDriveLogo from "@/assets/google-drive-logo.png";
import googleCalendarLogo from "@/assets/google-calendar-logo.png";
import zoomLogo from "@/assets/zoom-logo.png";
import googleMeetLogo from "@/assets/google-meet-logo.png";

const availableIntegrations = [
  {
    name: "Google Drive",
    logo: googleDriveLogo,
    description: "Store and manage PTA documents securely in the cloud. Share files with committee members, collaborate on documents in real-time, and maintain organized folder structures for meeting minutes, policies, financial records, and event planning materials. Keep your PTA's documentation accessible and well-organized.",
    status: "available"
  },
  {
    name: "Google Calendar",
    logo: googleCalendarLogo,
    description: "Seamlessly sync PTA events with Google Calendar to keep everyone informed. Automatically create calendar invites for meetings, share event schedules with members and parents, send reminders for important dates, and ensure no one misses crucial PTA activities or deadlines.",
    status: "available"
  }
];

const plannedIntegrations = [
  {
    name: "Zoom",
    logo: zoomLogo,
    description: "Host professional virtual PTA meetings with video conferencing capabilities. Organize online events and fundraisers, conduct committee meetings remotely, enable hybrid participation for members who cannot attend in person, and record sessions for those unable to join live.",
    status: "planned"
  },
  {
    name: "Google Meet",
    logo: googleMeetLogo,
    description: "Facilitate seamless video conferencing for PTA meetings directly through your Google Workspace. Enable remote collaboration, conduct online parent consultations, host virtual coffee mornings, and provide flexible meeting options for busy parents with time constraints.",
    status: "planned"
  },
  {
    name: "Swift Aid",
    logo: null,
    description: "Streamline first aid and incident management for school events. Track incident reports efficiently, maintain compliance records, coordinate with school health services, ensure proper documentation of student welfare matters, and keep parents informed of any incidents during PTA-organized activities.",
    status: "planned"
  }
];

const Integrations = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <section className="py-20 bg-gradient-to-b from-background to-muted/20">
          <div className="container">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Platform Integrations
              </h1>
              <p className="text-xl text-muted-foreground">
                Connect PTA Hub with the tools you already use to streamline your workflow and enhance collaboration.
              </p>
            </div>

            {/* Available Integrations */}
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <h2 className="text-3xl font-bold">Available Integrations</h2>
                <Badge variant="default" className="bg-primary">Live</Badge>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {availableIntegrations.map((integration) => (
                  <Card key={integration.name} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-center gap-4 mb-2">
                        {integration.logo && (
                          <img 
                            src={integration.logo} 
                            alt={`${integration.name} logo`} 
                            className="w-12 h-12"
                          />
                        )}
                        <CardTitle className="text-2xl">{integration.name}</CardTitle>
                      </div>
                      <CardDescription className="text-base leading-relaxed">
                        {integration.description}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </div>

            {/* Planned Integrations */}
            <div>
              <div className="flex items-center gap-3 mb-8">
                <h2 className="text-3xl font-bold">Planned Integrations</h2>
                <Badge variant="secondary">Coming Soon</Badge>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {plannedIntegrations.map((integration) => (
                  <Card key={integration.name} className="opacity-90 hover:opacity-100 transition-opacity">
                    <CardHeader>
                      <div className="flex items-center gap-4 mb-2">
                        {integration.logo ? (
                          <img 
                            src={integration.logo} 
                            alt={`${integration.name} logo`} 
                            className="w-12 h-12"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
                            <span className="text-lg font-bold text-muted-foreground">
                              {integration.name.charAt(0)}
                            </span>
                          </div>
                        )}
                        <CardTitle className="text-xl">{integration.name}</CardTitle>
                      </div>
                      <CardDescription className="text-base leading-relaxed">
                        {integration.description}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </div>

            <div className="mt-16 text-center">
              <Card className="inline-block p-8 bg-muted/30">
                <CardContent className="p-0">
                  <p className="text-lg mb-4">
                    Want to connect these integrations to your PTA?
                  </p>
                  <p className="text-muted-foreground">
                    PTA Admins can configure integrations from the{" "}
                    <Link to="/pta-admin" className="text-primary hover:underline font-semibold">
                      PTA Admin Dashboard
                    </Link>
                    {" "}under the Integrations tab.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
    </div>
  );
};

export default Integrations;
